/* moa-visor3d.js · «Personaliza tu coche» · MOA-Grafics · piloto
 *
 * El cliente elige color de carrocería y productos y gira el coche con el dedo.
 * NO coloca, mueve ni recorta nada: cada producto sale ya puesto en su sitio,
 * porque el sitio lo prepara MOA (ver herramientas/limpiar_exportar.py y la
 * página interna colocar/).
 *
 * Contrato con la malla: los materiales llamados MOA_* los pinta este fichero;
 * el resto (llantas, faros, pilotos) se queda como viene en el GLB.
 * - MOA_Parasol es la parte alta de la luna: cualquier tira de 6:1 entra ahí.
 * - MOA_Piel_<zona> son las «pieles» donde va el kit: copias de la chapa con un
 *   lienzo en metros. Hay cuatro —lateral, superior, delantera y trasera—,
 *   cada una desenrollada en su dirección para que una pieza no se deforme.
 *   Sus medidas están en recursos/coches/<coche>.json.
 * El kit no es una imagen única: es una lista de piezas (recursos/kits/<id>.piezas.json),
 * cada una con su zona, su centro en metros y su giro. Aquí se pintan sobre la piel.
 */
import * as THREE from './moa-3d-three.module.js';
import { GLTFLoader } from './moa-3d-GLTFLoader.js';
import { DRACOLoader } from './moa-3d-DRACOLoader.js';
import { OrbitControls } from './moa-3d-OrbitControls.js';

const raiz = document.querySelector('[data-moa-visor]');
const $ = s => raiz.querySelector(s);
const q = new URLSearchParams(location.search);
const num = (k, d) => q.has(k) ? Number(q.get(k)) : d;
const grados = Math.PI / 180;
const FONDO = '#2a2a2c';
const RECURSOS = raiz.dataset.baseRecursos || '';
// En el tema de Shopify los ficheros no viven en carpetas: cada uno tiene su dirección propia, con versión.
// La sección las deja en una tabla (<script class="pv-rutas">) y aquí se consulta. En la maqueta no hay tabla.
const RUTAS = JSON.parse((raiz.querySelector('script.pv-rutas') || {}).textContent || '{}');
const ruta = r => RUTAS[r] || RECURSOS + r;
// Cuánto se puede acercar ya no es un número fijo: depende de desde dónde se mire (ver `cerca()`).

// Colores propios, con nombre corriente. Nunca nombres de color de fabricante.
const COLORES = [
  { id: 'negro', nombre: 'Negro', hex: '#1a1a1a' },
  { id: 'blanco', nombre: 'Blanco', hex: '#f2f2f0' },
  { id: 'plata', nombre: 'Plata', hex: '#b9bcc0', metal: true },
  { id: 'gris', nombre: 'Gris', hex: '#4b4f55', metal: true },
  { id: 'rojo', nombre: 'Rojo', hex: '#c8102e' },
  { id: 'naranja', nombre: 'Naranja', hex: '#e2571c' },
  { id: 'amarillo', nombre: 'Amarillo', hex: '#f2c200' },
  { id: 'verde', nombre: 'Verde', hex: '#1d5a3a', metal: true },
  { id: 'azul', nombre: 'Azul', hex: '#1f3f8f', metal: true },
  { id: 'morado', nombre: 'Morado', hex: '#4918a0', metal: true }
];

// Los colores del KIT (Mou, 2026-10-07: «añade los kits en el 3D»). Son los siete en que se venden, en el orden de
// la tienda; no son los de la carrocería. Qué color de cada kit cambia lo dice recursos/kits/colores.json, que mide
// herramientas/kit_colores.py en las piezas. La «Plata espejo» (antes «Cromado»; vinilo S3101 Smooth Silver) no es
// un gris: se pinta METÁLICA, con el reflejo del estudio. Los alias son los nombres con que puede venir la opción
// Color de la ficha, para encontrar su variante sin escribir aquí ningún identificador.
const COLORES_KIT = [
  { id: 'blanco', nombre: 'Blanco', hex: '#ffffff', alias: ['blanco'] },
  { id: 'negro', nombre: 'Negro', hex: '#191919', alias: ['negro'] },
  { id: 'plata-espejo', nombre: 'Plata espejo', hex: '#d8d8d4', metal: true, alias: ['plata espejo', 'plata', 'cromado', 'silver'] },
  { id: 'gris', nombre: 'Gris perla', hex: '#aeb9c1', alias: ['gris perla', 'gris'] },
  { id: 'azul', nombre: 'Azul turquesa', hex: '#00adbf', alias: ['azul turquesa', 'azul'] },
  { id: 'rosa', nombre: 'Rosa flúor', hex: '#f906ed', alias: ['rosa fluor', 'rosa'] },
  { id: 'lila', nombre: 'Lila', hex: '#5600dd', alias: ['lila', 'morado'] }
];
// El metal de la Plata espejo. Elegido mirando las hojas de pruebas/hoja_colores_kits.html (2026-10-07): con el
// entorno del estudio, el costado reflejaba la sala oscura y la plata salía gris oscuro de lado y NEGRA de tres
// cuartos, igual que el Negro. Con su propio entorno (`estudioCromo()`) y rugosidad 0,2 se lee plata, con bandas,
// sobre coche blanco y sobre coche oscuro y desde cualquier lado.
// `cromo_r`, `cromo_e` y `cromo_m` en la dirección los cambian, solo para afinarlos mirando (pruebas/).
const CROMO = { rugosidad: num('cromo_r', 0.2), entorno: num('cromo_e', 1), metal: num('cromo_m', 1) };

const lienzo = $('.pv-escena canvas');
const escenario = $('.pv-escena');
const estadoTexto = $('.pv-estado');

const renderer = new THREE.WebGLRenderer({ canvas: lienzo, antialias: true, preserveDrawingBuffer: q.get('ui') === '0' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.toneMapping = THREE.NeutralToneMapping;   // neutro: aquí se venden colores y ACES los vira

const escena = new THREE.Scene();
// El estudio se pinta del color que la hoja de estilos le da a la caja de la escena: así el coche queda
// apoyado en la página, sea del color que sea, y es el bloque el que se adapta a la web y no al revés.
// Si la caja no trae color, vale el grafito de siempre.
const fondoCaja = getComputedStyle(escenario).backgroundColor;
escena.background = /^rgb\(/.test(fondoCaja) ? new THREE.Color().setStyle(fondoCaja) : new THREE.Color(FONDO);
const FONDO_CLARO = escena.background.getHSL({}).l > 0.6;
const camara = new THREE.PerspectiveCamera(32, 4 / 3, 0.1, 100);

/* ---- luz: un estudio de paneles, fabricado aquí. Sin fichero que descargar. ---- */
function estudio() {
  const s = new THREE.Scene();
  const sala = new THREE.Mesh(new THREE.BoxGeometry(30, 14, 30),
    new THREE.MeshBasicMaterial({ color: 0x19191b, side: THREE.BackSide }));
  sala.position.y = 6.5;
  s.add(sala);
  function panel(ancho, alto, x, y, z, rx, ry, fuerza) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(ancho, alto),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(fuerza, fuerza, fuerza), side: THREE.DoubleSide }));
    m.position.set(x, y, z);
    m.rotation.set(rx, ry, 0);
    s.add(m);
  }
  panel(8, 15, 0, 9, 0, Math.PI / 2, 0, 5.5);        // techo: dibuja capó, techo y hombros
  panel(15, 2.4, -9, 3.2, 0, 0, Math.PI / 2, 4);     // tira del lado izquierdo
  panel(15, 2.4, 9, 3.2, 0, 0, -Math.PI / 2, 4);     // tira del lado derecho
  panel(7, 3, 0, 4, 12, 0, Math.PI, 3);              // delante
  panel(7, 3, 0, 4, -12, 0, 0, 2);                   // detrás
  return s;
}
const pm = new THREE.PMREMGenerator(renderer);
escena.environment = pm.fromScene(estudio(), 0.02).texture;
pm.dispose();

// Lo que refleja la Plata espejo. El estudio de arriba es una sala casi negra con paneles: un espejo de verdad ahí
// sale negro en cuanto el costado no mira a un panel (visto de tres cuartos la plata era igual que el Negro).
// La bobina S3101 se ve plata CLARA y cálida con bandas anchas de blanco y gris, sin negros (paso6_producir.py,
// `cromo()`, cuarta versión). Así que el kit plateado refleja su propio entorno: sala gris clara, suelo cálido, una
// franja de horizonte más oscura y los mismos paneles. Solo lo usa el material de la piel cuando hay plata.
let entornoCromo = null;
function estudioCromo() {
  if (entornoCromo) return entornoCromo;
  const s = new THREE.Scene();
  const caja = (geo, color, y) => { const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, side: THREE.BackSide })); m.position.y = y; s.add(m); };
  caja(new THREE.BoxGeometry(30, 14, 30), 0x9c9c98, 6.5);                                    // sala: gris claro
  caja(new THREE.CylinderGeometry(14, 14, 2.6, 48, 1, true), 0x3a3835, 1.5);                 // horizonte: banda oscura
  caja(new THREE.CylinderGeometry(14, 14, 1.2, 48, 1, true), 0x6a6864, 5.2);                 // y otra más arriba, más suave
  const suelo = new THREE.Mesh(new THREE.PlaneGeometry(30, 30), new THREE.MeshBasicMaterial({ color: 0x8c877d }));
  suelo.rotation.x = -Math.PI / 2; suelo.position.y = 0.01; s.add(suelo);                     // suelo: cálido
  const panel = (an, al, x, y, z, rx, ry, f) => { const m = new THREE.Mesh(new THREE.PlaneGeometry(an, al),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(f, f, f), side: THREE.DoubleSide })); m.position.set(x, y, z); m.rotation.set(rx, ry, 0); s.add(m); };
  panel(8, 15, 0, 9, 0, Math.PI / 2, 0, 3);
  panel(15, 2.4, -9, 3.2, 0, 0, Math.PI / 2, 2.5);
  panel(15, 2.4, 9, 3.2, 0, 0, -Math.PI / 2, 2.5);
  panel(7, 3, 0, 4, 12, 0, Math.PI, 2);
  panel(7, 3, 0, 4, -12, 0, 0, 2);
  const g = new THREE.PMREMGenerator(renderer);
  entornoCromo = g.fromScene(s, 0.02).texture;
  g.dispose();
  return entornoCromo;
}

/* ---- materiales ---- */
const pintura = new THREE.MeshPhysicalMaterial({ color: '#1a1a1a', metalness: 0, roughness: 0.5, clearcoat: 1, clearcoatRoughness: 0.03 });
const vinilo = new THREE.MeshPhysicalMaterial({ metalness: 0, roughness: 0.9, specularIntensity: 0.2,
  // Casi sin reflejo: la luna mira al panel del techo y, visto desde arriba, el negro del diseño salía gris.
  // Bajar solo la intensidad del entorno (0,7 → 0,35 → 0,22) no bastó; lo que lo quita es el brillo.
  envMapIntensity: 0.6, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
// El kit: vinilo laminado en brillo sobre la chapa. Cada piel lleva una copia de este material con su lienzo.
const adhesivo = new THREE.MeshPhysicalMaterial({ metalness: 0, roughness: 0.5, clearcoat: 0.6, clearcoatRoughness: 0.05,
  envMapIntensity: 0.62,    // con 0,8 las llamas del capó, que miran al panel del techo, salían más claras que su muestra
  transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
const MATERIALES = {
  MOA_Pintura: pintura,
  MOA_Parasol: vinilo,
  MOA_Cristal: new THREE.MeshPhysicalMaterial({ color: 0x0b0f10, metalness: 0, roughness: 0, transparent: true, opacity: 0.42, envMapIntensity: 1.5, depthWrite: false }),
  // Lunas tintadas, para los coches marcados con `cristal_tintado` en la lista: los que traen el interior con texturas
  // rotas o pobres (Mou, 2026-10-11, con el MR2: «tinta los cristales para que no se vea porque tiene texturas rotas»).
  MOA_Cristal_tintado: new THREE.MeshPhysicalMaterial({ color: 0x050607, metalness: 0, roughness: 0, transparent: true, opacity: 0.86, envMapIntensity: 1.5, depthWrite: false }),
  MOA_Interior: new THREE.MeshStandardMaterial({ color: 0x101010, roughness: 0.9 }),
  MOA_Plastico: new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.6 }),
  // Para las mallas cuyo neumático llega sin textura (el E46: su imagen no venía dentro del fichero y salía blanco).
  MOA_Goma: new THREE.MeshStandardMaterial({ color: 0x0c0c0c, roughness: 0.88 })
};

function sombra(ancho, largo) {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const x = c.getContext('2d'), g = x.createRadialGradient(128, 128, 20, 128, 128, 128);
  // Sobre fondo claro la sombra se ve entera: tiene que ser corta y morir antes del borde del cuadro.
  // Con la de fondo oscuro (ancha y densa) se veía el corte recto del lienzo sobre el blanco.
  if (FONDO_CLARO) { g.addColorStop(0, 'rgba(0,0,0,0.55)'); g.addColorStop(0.6, 'rgba(0,0,0,0.26)'); g.addColorStop(0.85, 'rgba(0,0,0,0.05)'); g.addColorStop(1, 'rgba(0,0,0,0)'); }
  else { g.addColorStop(0, 'rgba(0,0,0,0.75)'); g.addColorStop(0.6, 'rgba(0,0,0,0.35)'); g.addColorStop(1, 'rgba(0,0,0,0)'); }
  x.fillStyle = g; x.fillRect(0, 0, 256, 256);
  const m = new THREE.Mesh(new THREE.PlaneGeometry(ancho, largo),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(c), transparent: true, depthWrite: false }));
  m.rotation.x = -Math.PI / 2; m.position.y = 0.002;
  return m;
}

/* ---- giro con el dedo: sin desplazar y sin bajar del suelo ---- */
const controles = new OrbitControls(camara, lienzo);
controles.enablePan = false;
controles.enableDamping = true;
controles.dampingFactor = 0.08;
controles.rotateSpeed = 0.7;
controles.minPolarAngle = 25 * grados;     // casi desde arriba, pero no en picado
controles.maxPolarAngle = 86 * grados;     // nunca por debajo del coche
controles.target.set(0, 0.55, 0);
// Cada cambio de los controles pide un cuadro. Sin esto el zoom iba a tirones: OrbitControls aplica el zoom
// dentro de su propio manejador, así que cuando el bucle volvía a preguntar ya «no había cambiado nada» y ese
// cuadro no se pintaba (Mou, 2026-10-01: «el zoom va como a pocos frames»). El giro no lo sufría porque lleva
// inercia y sigue cambiando entre cuadro y cuadro.
controles.addEventListener('change', () => { pendiente = true; });
// Con dos dedos manda el coche, no la página: si no, el navegador intenta desplazarla y corta el gesto.
['touchstart', 'touchmove'].forEach(n => lienzo.addEventListener(n, e => { if (e.touches.length > 1 && e.cancelable) e.preventDefault(); }, { passive: false }));
// Lo más que se deja acercar la cámara, según desde dónde mira: hasta medio metro de la chapa, sin meterse dentro.
// De lado el coche es estrecho y se llega cerca; de frente es largo y la cámara se queda más lejos del centro.
// (Antes era una distancia fija, pensada para el morro, y de lado se quedaba lejos: «me tiene que dejar hacer más zoom».)
function cerca() {
  const m = estado.medidas;
  if (!m) return 2;
  const d = camara.position.clone().sub(controles.target), h = Math.hypot(d.x, d.z) || 1;
  return 1 / Math.hypot(d.x / h / (m.x / 2 + 0.55), d.z / h / (m.z / 2 + 0.6));
}

const estado = { listo: false, color: null, parasol: null, franja: null, kit: null, colorKit: null, piezas: [], marco: null, marcoLado: 'ambos', coche: null, cocheId: null, montado: null, alCambiarKit: null };
const pieles = {};                         // zona → { malla, tela, ctx, textura, def }
let pxPorMetro = 600;
let defs = {};                             // zona → medidas de su lienzo (recursos/coches/<coche>.json)
// Cada pieza del kit existe dos veces, una por lado del coche. Lo normal es que vayan enlazadas:
// la derecha es el espejo de la izquierda. Si la pieza trae `der`, la derecha tiene su propio sitio.
// El sitio se guarda SIEMPRE como si fuera del lado izquierdo; la derecha se pinta reflejada.
const caraDe = (p, lado) => (lado === 'der' && p.der) ? p.der : p;
let pendiente = true;
const pedir = () => { pendiente = true; };

function encuadrar() {
  const w = escenario.clientWidth, h = escenario.clientHeight;
  if (!w || !h) return;
  renderer.setSize(w, h, false);
  camara.aspect = w / h;
  camara.updateProjectionMatrix();
  // La distancia que deja el coche entero en cuadro: en vertical se aleja.
  const mitad = Math.atan(Math.tan(camara.fov * grados / 2) * camara.aspect);
  // 2,5 m de media anchura visible: con 2,75 el coche quedaba pequeño en el
  // cuadro y con 2,3 se salía el paragolpes por un lado.
  const dist = Math.max(4.8, 2.5 / Math.tan(mitad));
  controles.maxDistance = dist * 1.35;
  if (!estado.colocada) {
    const az = num('az', 36) * grados, el = num('el', 11) * grados, d = num('dist', dist);
    camara.position.set(Math.sin(az) * Math.cos(el) * d, 0.55 + Math.sin(el) * d, Math.cos(az) * Math.cos(el) * d);
    estado.colocada = true;
  }
  controles.update();
  pedir();
}
new ResizeObserver(encuadrar).observe(escenario);

function bucle() {
  requestAnimationFrame(bucle);
  controles.minDistance = cerca();
  if (controles.update() || pendiente) {
    pendiente = false;
    renderer.render(escena, camara);
  }
}

/* ---- lo que elige el cliente ---- */
function ponerColor(c) {
  estado.color = c;
  pintura.color.set(c.hex);
  pintura.metalness = c.metal ? 0.85 : 0;
  pintura.roughness = c.metal ? 0.45 : 0.5;
  raiz.querySelectorAll('.pv-color').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === c.id)));
  $('.pv-color-nombre').textContent = c.nombre;
  pedir();
}

const texturas = {};
function ponerParasol(d) {
  estado.parasol = d;
  raiz.querySelectorAll('.pv-tira').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === (d ? d.id : ''))));
  $('.pv-elegido').textContent = d ? d.nombre + ' · ' + d.variante : 'Sin parasol';
  const ficha = $('.pv-ficha');
  ficha.hidden = !d;
  if (d) ficha.href = (raiz.dataset.baseProducto || '/products/') + d.handle;
  pintarCarrito();
  if (!estado.franja) return;
  if (!d) { estado.franja.visible = false; pedir(); return; }
  const lista = t => {
    if (estado.parasol !== d) return;          // el cliente ya ha elegido otro
    vinilo.map = t; vinilo.needsUpdate = true;
    estado.franja.visible = true;
    pedir();
  };
  if (texturas[d.id]) { lista(texturas[d.id]); return; }
  new THREE.TextureLoader().load(ruta(d.tira), t => {
    t.colorSpace = THREE.SRGBColorSpace;
    t.flipY = false;
    t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    texturas[d.id] = t;
    lista(t);
  });
}

/* ---- el kit: piezas pintadas sobre las pieles ---- */
// De metros de la piel a píxeles de su lienzo, y al revés. Cada piel dice hacia dónde crecen sus ejes.
function aPx(zona, c) {
  const d = defs[zona];
  return [(d.u_al_reves ? d.u[1] - c[0] : c[0] - d.u[0]) * pxPorMetro, (d.v_arriba ? d.v[1] - c[1] : c[1] - d.v[0]) * pxPorMetro];
}
function aMetros(zona, x, y) {
  const d = defs[zona];
  return [d.u_al_reves ? d.u[1] - x / pxPorMetro : d.u[0] + x / pxPorMetro, d.v_arriba ? d.v[1] - y / pxPorMetro : d.v[0] + y / pxPorMetro];
}
// El otro lado del coche. En la piel lateral las dos caras comparten lienzo y no hay que hacer nada;
// en las demás, la pieza se repite reflejada sobre el eje del coche.
function reflejo(zona, x, y) {
  const d = defs[zona], o = aPx(zona, [0, 0]);
  return d.espejo === 'v' ? [x, 2 * o[1] - y] : d.espejo === 'u' ? [2 * o[0] - x, y] : [x, y];
}

/* ---- el color del kit: se recolorea la pieza en el navegador, sin un PNG por color ---- */
// Cada píxel de la pieza se lee como mezcla de DOS colores de su paleta (el borde entre el negro y el rosa de un
// tribal es medio negro y medio rosa); se cambia solo la parte del principal y se vuelve a mezclar. Así el borde
// sigue suave y lo que no es el principal (estrellas, nubes, el blanco del Racing Style) no se toca.
// Con `invertir_rosa`, el Rosa cambia también el rosa del kit por el negro: negro→rosa y rosa→negro (como las
// imágenes del coche de staging/kits_seis_colores). Para la Plata espejo sale además una máscara de metal.
const rgb = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
let COLORES_DE_KIT = {};                    // id del kit → su entrada de colores.json (en el tema viene dentro del kit)
const coloresDe = d => (d && (d.colores || COLORES_DE_KIT[d.id])) || null;
const recoloreadas = new Map();             // imagen + color → { color: lienzo, metal: lienzo | null }
function recolorear(img, cfg, c) {
  const clave = img.src + '|' + c.id;
  if (recoloreadas.has(clave)) return recoloreadas.get(clave);
  const w = img.naturalWidth, h = img.naturalHeight, tela = document.createElement('canvas');
  tela.width = w; tela.height = h;
  const ctx = tela.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const datos = ctx.getImageData(0, 0, w, h), px = datos.data;
  const P = cfg.paleta.map(rgb), ip = Math.max(0, cfg.paleta.indexOf(cfg.principal));
  const M = P.map(v => v.slice()), metal = P.map(() => 0);
  // En el color con el que se vende, el principal se queda como está (solo se recolorea para abrir el hueco).
  M[ip] = c.id === cfg.original ? P[ip].slice() : rgb(c.hex);
  if (c.metal) metal[ip] = CROMO.metal;
  // EL HUECO (Mou, 2026-10-07): en los kits de letras «RACING» el blanco no es vinilo, es la chapa que se ve. Se
  // vuelve transparente y se ve la carrocería del color que sea.
  const opaco = cfg.paleta.map(h => (cfg.hueco || []).includes(h) ? 0 : 1);
  const conHueco = opaco.includes(0);
  // Otro tono del mismo principal (el cuadrado de rayas de Japan Racing): cambia con él.
  (cfg.tambien || []).forEach(h => {
    const j = cfg.paleta.indexOf(h);
    if (j >= 0 && j !== ip) { M[j] = rgb(c.hex); if (c.metal) metal[j] = CROMO.metal; }
  });
  if (c.id === 'rosa' && cfg.invertir_rosa) {
    const ir = cfg.paleta.indexOf(cfg.invertir_rosa);
    // El rosa pasa al negro del kit; si el kit no es negro (el Lila de Tribal Stars), al Negro de la tienda.
    if (ir >= 0) M[ir] = cfg.original === 'negro' ? P[ip].slice() : rgb(COLORES_KIT.find(k => k.id === 'negro').hex);
  }
  // Regla general (Mou, 2026-10-07): si el color elegido es el de una capa secundaria del kit (la sombra negra de
  // Japan Grey Flames en el Negro), esa capa pasa al color original del principal. Lo mide herramientas/kit_colores.py.
  if (cfg.intercambia && cfg.intercambia[c.id]) {
    const j = cfg.paleta.indexOf(cfg.intercambia[c.id]);
    if (j >= 0 && j !== ip) M[j] = P[ip].slice();
  }
  // Las parejas posibles (y cada color solo): con tres o cuatro colores son pocas.
  const parejas = [];
  for (let a = 0; a < P.length; a++) for (let b = a; b < P.length; b++) {
    const d = [P[b][0] - P[a][0], P[b][1] - P[a][1], P[b][2] - P[a][2]];
    parejas.push([a, b, d, d[0] * d[0] + d[1] * d[1] + d[2] * d[2]]);
  }
  let mtela = null, mpx = null;
  if (c.metal) { mtela = document.createElement('canvas'); mtela.width = w; mtela.height = h; mpx = new Uint8ClampedArray(px.length); }
  const rCromo = Math.round(CROMO.rugosidad * 255), rVinilo = Math.round(adhesivo.roughness * 255);
  for (let i = 0; i < px.length; i += 4) {
    if (!px[i + 3]) continue;
    const r = px[i], g = px[i + 1], b = px[i + 2];
    let mejor = Infinity, A = 0, B = 0, s = 0;
    for (const [a, bb, d, dd] of parejas) {
      const ox = r - P[a][0], oy = g - P[a][1], oz = b - P[a][2];
      let t = dd ? (ox * d[0] + oy * d[1] + oz * d[2]) / dd : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const ex = ox - t * d[0], ey = oy - t * d[1], ez = oz - t * d[2], e = ex * ex + ey * ey + ez * ez;
      if (e < mejor) { mejor = e; A = a; B = bb; s = t; if (!e) break; }
    }
    // Lo que el par no explica (ruido, compresión) se conserva encima del color nuevo.
    for (let k = 0; k < 3; k++) {
      const plano = P[A][k] + s * (P[B][k] - P[A][k]);
      px[i + k] = (1 - s) * M[A][k] + s * M[B][k] + (px[i + k] - plano);
    }
    if (conHueco) px[i + 3] = px[i + 3] * ((1 - s) * opaco[A] + s * opaco[B]);
    if (mpx) {
      const m = (1 - s) * metal[A] + s * metal[B];
      mpx[i + 1] = rVinilo + m * (rCromo - rVinilo);   // verde: rugosidad (así la lee three.js)
      mpx[i + 2] = m * 255;                             // azul: metal
      mpx[i + 3] = px[i + 3];
    }
  }
  ctx.putImageData(datos, 0, 0);
  if (mtela) mtela.getContext('2d').putImageData(new ImageData(mpx, w, h), 0, 0);
  const hecho = { color: tela, metal: mtela };
  recoloreadas.set(clave, hecho);
  return hecho;
}
// El color elegido para el kit puesto, o null si va como se vende hoy (entonces se pinta el PNG tal cual).
function colorKitActivo() {
  const cfg = coloresDe(estado.kit), c = cfg && COLORES_KIT.find(k => k.id === estado.colorKit);
  // Mientras llegan las piezas de otro kit siguen puestas las del anterior: no se recolorean con la paleta del nuevo.
  // Con hueco se recolorea también en el color original: el PNG trae el hueco pintado de blanco.
  return c && (c.id !== cfg.original || (cfg.hueco && cfg.hueco.length)) && estado.piezasKit === estado.kit ? { cfg, c } : null;
}
function capaDe(p, capa) {
  const act = colorKitActivo();
  if (!act) return capa === 'metal' ? null : p.img;
  const hecho = recolorear(p.img, act.cfg, act.c);
  return capa === 'metal' ? hecho.metal : hecho.color;
}

function dibujar(piel, p, lado, capa = 'color') {
  const zona = piel.zona, s = caraDe(p, lado), reflejada = lado === 'der' && defs[zona].espejo !== 'uv';
  const imagen = capaDe(p, capa);
  if (!imagen) return;
  const ctx = capa === 'metal' ? piel.metal.ctx : piel.ctx, [x, y] = aPx(zona, s.centro_m), w = p.ancho_m * pxPorMetro, h = p.alto_m * pxPorMetro;
  ctx.save();
  if (reflejada) {
    const o = aPx(zona, [0, 0]);
    if (defs[zona].espejo === 'v') { ctx.translate(0, 2 * o[1]); ctx.scale(1, -1); } else { ctx.translate(2 * o[0], 0); ctx.scale(-1, 1); }
  }
  ctx.translate(x, y);
  ctx.rotate(-s.giro_grados * grados);          // mismo sentido que el guion: positivo, contra el reloj
  // Una pieza con letras no va en espejo en el lado derecho: el kit trae para ese lado otra igual, que se lee.
  // En el costado la piel derecha ya refleja el lienzo entero; aquí se le da la vuelta a la pieza para deshacerlo.
  if (p.sin_espejo && lado === 'der' && defs[zona].espejo === 'uv') ctx.scale(-1, 1);
  // Solo al colocar: la pieza recién elegida parpadea suave hasta que se toca (lo lleva colocar.js en `estado.pulso`).
  if (p === estado.marco && estado.pulso != null) ctx.globalAlpha = estado.pulso;
  ctx.drawImage(imagen, -w / 2, -h / 2, w, h);
  ctx.globalAlpha = 1;
  if (capa === 'color' && p === estado.marco && (estado.marcoLado === 'ambos' || estado.marcoLado === lado)) {                      // solo en la página interna de colocar: el marco de la pieza elegida
    ctx.strokeStyle = '#ff2d78'; ctx.lineWidth = 3; ctx.setLineDash([14, 10]);
    ctx.strokeRect(-w / 2 - 6, -h / 2 - 6, w + 12, h + 12);
  }
  ctx.restore();
}

function pintarPieles(solo) {
  Object.values(pieles).forEach(piel => {
    if (solo && !solo.includes(piel.zona)) return;
    const suyas = [];
    estado.piezas.forEach(p => ['izq', 'der'].forEach(lado => {
      if (caraDe(p, lado).zona === piel.zona && (!piel.lado || piel.lado === lado)) suyas.push([p, lado]);
    }));
    if (!suyas.length && !piel.pintada) { piel.malla.visible = false; return; }
    piel.ctx.clearRect(0, 0, piel.tela.width, piel.tela.height);
    suyas.forEach(([p, lado]) => dibujar(piel, p, lado));
    metalDePiel(piel, suyas);
    piel.pintada = suyas.length > 0;
    piel.textura.needsUpdate = true;
    piel.malla.visible = suyas.length > 0;
  });
  pedir();
}

// La Plata espejo: la piel lleva un segundo lienzo, del mismo tamaño, que dice dónde es metal (azul) y cuánto
// brilla (verde), y el material lo usa de mapa de metal y de rugosidad. Solo se crea si alguien elige ese color.
function metalDePiel(piel, suyas) {
  const act = colorKitActivo(), conMetal = !!(act && act.c.metal && suyas.length);
  const m = piel.malla.material;
  if (conMetal) {
    if (!piel.metal) {
      const tela = document.createElement('canvas');
      tela.width = piel.tela.width; tela.height = piel.tela.height;
      const textura = new THREE.CanvasTexture(tela);     // datos, no color: sin espacio de color
      textura.flipY = false;
      textura.anisotropy = renderer.capabilities.getMaxAnisotropy();
      piel.metal = { tela, ctx: tela.getContext('2d'), textura };
    }
    piel.metal.ctx.clearRect(0, 0, piel.metal.tela.width, piel.metal.tela.height);
    suyas.forEach(([p, lado]) => dibujar(piel, p, lado, 'metal'));
    piel.metal.textura.needsUpdate = true;
  }
  const antes = !!m.metalnessMap;
  m.metalnessMap = m.roughnessMap = conMetal ? piel.metal.textura : null;
  m.metalness = conMetal ? 1 : adhesivo.metalness;
  m.roughness = conMetal ? 1 : adhesivo.roughness;
  m.envMapIntensity = conMetal ? CROMO.entorno : adhesivo.envMapIntensity;
  m.envMap = conMetal ? estudioCromo() : null;          // sin entorno propio vale el de la escena
  if (antes !== conMetal) m.needsUpdate = true;
}

function cargarImagen(src) {
  // crossOrigin: en la tienda las piezas pueden venir de otro dominio, y sin esto el lienzo no se deja usar de textura.
  return new Promise((bien, mal) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => bien(i); i.onerror = () => mal(new Error('no carga ' + src)); i.src = src; });
}

const kitsCargados = {};
let KITS = [];
// Dónde va cada pieza depende del coche: cada kit trae una lista de piezas por coche (kits.json, `piezas_por_coche`).
// Un kit sin lista para el coche elegido no se ofrece en ese coche. Sin ese mapa, vale la lista única de siempre.
// En el tema cada kit trae UN fichero con las listas de todos los coches (`juntas`, lo arma empaquetar_tema.py):
// con treinta kits y treinta coches, un fichero por pareja serían mil ficheros en el tema.
const piezasDe = d => d.juntas ? (d.coches.includes(estado.cocheId) ? d.juntas : null)
  : (d.piezas_por_coche ? d.piezas_por_coche[estado.cocheId] || null : d.piezas);
const juntasCargadas = {};
function ponerKit(d) {
  if (d && !piezasDe(d)) d = null;
  const fichero = d && piezasDe(d), coche = estado.cocheId, clave = d && d.id + '@' + coche;
  // Otro kit entra en el color con que se vende (el de su foto en la bandeja); el mismo kit conserva el elegido.
  const otro = d !== estado.kit;
  if (otro || !coloresDe(d)) estado.colorKit = coloresDe(d) ? coloresDe(d).original : null;
  estado.kit = d;
  if (otro) negroSiClaro();        // un kit que se vende en claro (Blanco o Plata espejo) también
  raiz.querySelectorAll('.pv-kit').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === (d ? d.id : ''))));
  rotularKit();
  pintarColoresKit();
  pintarCarrito();
  const puesto = piezas => {
    if (estado.kit !== d || estado.cocheId !== coche) return;       // el cliente ya ha elegido otro kit u otro coche
    estado.piezas = piezas;
    estado.piezasKit = d;
    pintarPieles();
    if (estado.alCambiarKit) estado.alCambiarKit();
  };
  if (!d) { puesto([]); return Promise.resolve(); }
  if (kitsCargados[clave]) { puesto(kitsCargados[clave]); return Promise.resolve(); }
  // Si la red falla un instante, se prueba otra vez; y si tampoco, el kit se quita en vez de quedarse «elegido»
  // sin una sola pieza a la vista (pasó el 2026-10-01 con el servidor de pruebas: kit marcado y coche sin llamas).
  const leer = () => fetch(ruta(fichero)).then(r => { if (!r.ok) throw new Error('piezas del kit: ' + r.status); return r.json(); });
  const lista = () => {
    if (!d.juntas) return leer();
    const todas = juntasCargadas[fichero] || (juntasCargadas[fichero] = leer().catch(e => { delete juntasCargadas[fichero]; throw e; }));
    return todas.then(j => ({ piezas: j[coche] }));
  };
  const traer = () => lista()
    .then(j => Promise.all(j.piezas.map(p => cargarImagen(ruta(p.imagen)).then(i => { p.img = i; }))).then(() => j.piezas));
  return traer().catch(() => new Promise(r => setTimeout(r, 700)).then(traer))
    .then(piezas => { kitsCargados[clave] = piezas; puesto(piezas); })
    .catch(e => { console.error(e); if (estado.kit === d && estado.cocheId === coche) ponerKit(null); });
}

// El nombre del kit en su fila, con el color si el kit se vende en colores: «Tribal · Azul turquesa».
// El enlace a la ficha lleva el color en `color_kit` (y la variante, si la tienda la tiene: ver pintarCarrito).
function rotularKit() {
  const d = estado.kit, c = coloresDe(d) && COLORES_KIT.find(k => k.id === estado.colorKit);
  $('.pv-kit-elegido').textContent = d ? d.nombre + (c ? ' · ' + c.nombre : '') : 'Sin kit';
  const ficha = $('.pv-ficha-kit');
  ficha.hidden = !d;
  if (d) ficha.href = (raiz.dataset.baseProducto || '/products/') + d.handle + (c ? '?color_kit=' + c.id : '');
}

// Las muestras del color del kit. Solo salen con un kit que se vende en colores.
// VAN DENTRO DEL BLOQUE FIJO DEL COCHE, justo debajo de él (Mou, 2026-10-07: «a menos que el cliente baje no le
// aparece el selector de colores de kit»). Antes iban debajo de la bandeja de kits, pegadas al pie de la pantalla:
// solo se pegaban con la bandeja ya a la vista, y en el móvil la barra del navegador tapa justo esa franja hasta que
// se baja. Debajo del coche se ven siempre, y el color se mira en el coche al cambiarlo. Donde no hay bloque fijo
// (las hojas de prueba), siguen debajo de la bandeja.
// EN EL ORDENADOR van dentro de la bandeja, entre «Sin kit» y la primera fila de kits (Mou, 2026-10-07): allí el
// coche va en su columna y la bandeja se ve entera al lado, así que no hace falta llevarlas al bloque del coche.
let cajaColoresKit = null;
const escritorio = window.matchMedia('(min-width: 1000px)');
function colocarColoresKit() {
  const kits = $('.pv-kits');
  if (!cajaColoresKit || !kits) return;
  const fijo = $('.pt-lado-escena'), ninguno = kits.querySelector('.pv-kit--ninguno');
  const enBandeja = !fijo || escritorio.matches;
  cajaColoresKit.classList.toggle('pv-kit-colores--fija', !enBandeja);
  cajaColoresKit.classList.toggle('pv-kit-colores--bandeja', enBandeja && !!ninguno);
  if (!enBandeja) fijo.appendChild(cajaColoresKit);
  else if (ninguno) ninguno.after(cajaColoresKit);
  else kits.after(cajaColoresKit);
}
escritorio.addEventListener('change', colocarColoresKit);
function pintarColoresKit() {
  const kits = $('.pv-kits');
  if (!kits) return;
  if (!cajaColoresKit) {
    cajaColoresKit = document.createElement('div');
    cajaColoresKit.className = 'pv-kit-colores';
    cajaColoresKit.setAttribute('role', 'group');
    cajaColoresKit.setAttribute('aria-label', 'Color del kit');
    const rotulo = document.createElement('p');
    rotulo.className = 'pv-kit-colores-rotulo';
    rotulo.append('Color del kit ');
    const nombre = document.createElement('span');
    nombre.className = 'pv-kit-color-nombre';
    rotulo.appendChild(nombre);
    const fila = document.createElement('div');
    fila.className = 'pv-kit-muestras';
    COLORES_KIT.forEach(c => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'pv-kit-color' + (c.metal ? ' pv-kit-color--metal' : ''); b.dataset.id = c.id;
      b.style.setProperty('--c', c.hex);
      b.setAttribute('aria-label', c.nombre); b.title = c.nombre; b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', () => ponerColorKit(c.id));
      fila.appendChild(b);
    });
    cajaColoresKit.append(rotulo, fila);
    colocarColoresKit();
  }
  const cfg = coloresDe(estado.kit);
  cajaColoresKit.hidden = !cfg;
  if (!cfg) return;
  cajaColoresKit.querySelectorAll('.pv-kit-color').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === estado.colorKit)));
  const c = COLORES_KIT.find(k => k.id === estado.colorKit);
  cajaColoresKit.querySelector('.pv-kit-color-nombre').textContent = c ? c.nombre : '';
}

function ponerColorKit(id) {
  if (!coloresDe(estado.kit) || !COLORES_KIT.some(k => k.id === id)) return;
  estado.colorKit = id;
  negroSiClaro();
  rotularKit();
  pintarColoresKit();
  pintarCarrito();
  pintarPieles();
}

// Regla de Mou del 2026-10-07: «cuando el cliente seleccione un color claro, tanto en la ficha de producto como en
// Personaliza tu coche, automáticamente se pondrá el coche de color negro». Un kit Blanco o Plata espejo sobre un
// coche blanco no se ve. La carrocería pasa sola a Negro (y el selector de carrocería se marca en Negro).
// El Gris perla también, SALVO que al kit en gris le quede alguna capa negra (Japan Grey Flames, Llamas Nubladas):
// ahí el coche se queda blanco, porque negro sobre negro no se ve (Mou, el mismo día). Cada kit trae su lista en
// `coche_negro` (herramientas/kit_colores.py); sin ella, Blanco y Plata espejo.
// Si DESPUÉS de ese cambio el cliente elige la carrocería a mano, manda lo suyo: ya no se le vuelve a cambiar.
// Al pasar a un color oscuro de kit no se le devuelve la carrocería que tenía.
const KIT_CLARO = ['blanco', 'plata-espejo'];
function negroSiClaro() {
  const cfg = coloresDe(estado.kit), claros = (cfg && cfg.coche_negro) || KIT_CLARO;
  if (!claros.includes(estado.colorKit) || estado.carroceriaAMano) return;
  estado.negroAutomatico = true;
  if (!estado.color || estado.color.id !== 'negro') ponerColor(COLORES.find(c => c.id === 'negro'));
}

// Debajo de la foto de un kit que se vende en colores, sus siete colores en puntos pequeños, como las tarjetas del
// catálogo de la tienda (Mou, 2026-10-07: «para que el cliente pueda interpretar antes de abrirlo que hay
// configuración de colores»). Son solo aviso: el color se elige debajo del coche, al tocar el kit.
function puntosDeColor(b, d) {
  if (!coloresDe(d) || b.querySelector('.pv-kit-puntos')) return;
  const fila = document.createElement('span');
  fila.className = 'pv-kit-puntos';
  fila.setAttribute('aria-hidden', 'true');
  COLORES_KIT.forEach(c => {
    const p = document.createElement('span');
    p.className = 'pv-kit-punto' + (c.metal ? ' pv-kit-punto--metal' : '');
    p.style.setProperty('--c', c.hex);
    fila.appendChild(p);
  });
  b.appendChild(fila);
  b.setAttribute('aria-label', d.nombre + ', en ' + COLORES_KIT.length + ' colores');
}

function pintarKits(kits) {
  KITS = kits;
  const caja = $('.pv-kits');
  // Solo «Universales» y la marca del coche (Mou, 2026-10-07): sin «Todos», que mezclaría las dos cosas.
  filtro(caja, [...new Set(kits.map(d => d.grupo).filter(Boolean))], 'Tipo de kit', false);
  [null].concat(kits).forEach(d => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = d ? 'pv-kit' : 'pv-kit pv-kit--ninguno'; b.dataset.id = d ? d.id : '';
    if (d && d.grupo) b.dataset.grupo = d.grupo;
    if (d && d.marca) b.dataset.marca = d.marca;
    b.setAttribute('aria-pressed', String(!d));
    if (d) {
      const muestra = document.createElement('i');
      muestra.style.setProperty('--c', d.color);
      b.appendChild(muestra);
      b.setAttribute('aria-label', d.nombre);
      // El kit se elige por la imagen de su ficha, como los parasoles por su tira (Mou, 2026-10-01: «en vez de
      // aparecer el nombre del kit, que me aparezca la imagen del producto»). La imagen se pide a la tienda;
      // mientras llega —o si no llega— se ve el nombre con su color.
      fichaTienda(d.handle).then(p => {
        if (!p || !p.featured_image) return;
        const i = new Image();
        i.alt = ''; i.width = 200; i.height = 200;
        i.onload = () => { b.textContent = ''; b.appendChild(i); b.classList.add('pv-kit--imagen'); puntosDeColor(b, d); };
        // La tienda da la dirección sin protocolo («//cdn…»): se pide siempre por conexión segura.
        const foto = p.featured_image.replace(/^\/\//, 'https://');
        i.src = foto + (foto.includes('?') ? '&' : '?') + 'width=400';
      });
    }
    b.appendChild(document.createTextNode(d ? d.nombre : 'Sin kit'));
    if (d) puntosDeColor(b, d);
    b.addEventListener('click', () => ponerKit(d));
    caja.appendChild(b);
  });
  // Como los parasoles: se abre en la pestaña del kit que viene en la dirección y, si no viene, en la primera.
  // Con 32 kits, «Todos» de entrada son once filas de fotos.
  const elegido = kits.find(d => d.id === q.get('kit'));
  verGrupo(caja, (elegido && elegido.grupo) || (kits.find(d => d.grupo) || {}).grupo || '');
  colocarColoresKit();          // si las muestras se crearon antes que «Sin kit», van ahora a su sitio
}

function pintarControles(catalogo) {
  const colores = $('.pv-colores');
  COLORES.forEach(c => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'pv-color'; b.dataset.id = c.id;
    b.style.setProperty('--c', c.hex);
    b.setAttribute('aria-label', c.nombre); b.setAttribute('aria-pressed', 'false');
    // Elegida a mano DESPUÉS de que un kit claro la pusiera en negro: desde ahí se respeta (ver negroSiClaro()).
    b.addEventListener('click', () => { if (estado.negroAutomatico) estado.carroceriaAMano = true; ponerColor(c); });
    colores.appendChild(b);
  });
  const tiras = $('.pv-tiras');
  // Orden de las pestañas (Mou, 2026-10-08): «primero de marca, luego de escena, luego de patrocinios y luego
  // videojuegos». De las de marca solo se ve la del coche elegido (soloDeLaMarca); «Todos» va al final.
  const ORDEN_PARASOL = ['Escena', 'Patrocinios', 'Videojuegos'];
  const deMarca = new Set(catalogo.filter(d => d.marca).map(d => d.grupo));
  const pesoGrupo = g => deMarca.has(g) ? 0 : ORDEN_PARASOL.includes(g) ? 1 + ORDEN_PARASOL.indexOf(g) : 9;
  const gruposParasol = [...new Set(catalogo.map(d => d.grupo).filter(Boolean))]
    .map((g, i) => [g, i]).sort((a, b) => pesoGrupo(a[0]) - pesoGrupo(b[0]) || a[1] - b[1]).map(x => x[0]);
  filtro(tiras, gruposParasol, 'Tipo de parasol', true);
  const ninguna = document.createElement('button');
  ninguna.type = 'button'; ninguna.className = 'pv-tira pv-tira--ninguna'; ninguna.dataset.id = '';
  ninguna.textContent = 'Sin parasol'; ninguna.setAttribute('aria-pressed', 'true');
  ninguna.addEventListener('click', () => ponerParasol(null));
  tiras.appendChild(ninguna);
  catalogo.forEach(d => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'pv-tira'; b.dataset.id = d.id;
    if (d.grupo) b.dataset.grupo = d.grupo;
    if (d.marca) b.dataset.marca = d.marca;
    b.setAttribute('aria-label', d.nombre + ', ' + d.variante); b.setAttribute('aria-pressed', 'false');
    const i = document.createElement('img');
    i.src = ruta(d.tira); i.alt = ''; i.loading = 'lazy'; i.width = 180; i.height = 30;
    b.appendChild(i);
    b.addEventListener('click', () => ponerParasol(d));
    tiras.appendChild(b);
  });
  // Se abre en la pestaña del parasol que viene elegido en la dirección; si no viene ninguno, en la primera del orden
  // de arriba (una de marca; si no es la del coche, soloDeLaMarca pasa a la primera visible: la marca o Escena).
  const elegido = catalogo.find(d => d.id === q.get('parasol'));
  verGrupo(tiras, (elegido && elegido.grupo) || gruposParasol[0] || '');
}

/* ---- las filas desplegables: la que se toca no se mueve del sitio ---- */
// Solo hay una abierta a la vez. Al abrir una de abajo se cierra la de arriba, todo sube de golpe y la recién
// abierta quedaba escondida detrás del coche, que en el móvil va fijo arriba (Mou, 2026-10-01: «la de kits sube
// de golpe y se deja de ver»). Se abre a mano y se compensa el salto en el mismo cuadro: el título se queda bajo
// el dedo. Después, si lo abierto no cabe en pantalla, sube lo justo, sin meter el título detrás del coche.
const filas = [...raiz.querySelectorAll('.pt-filas details')];
const bajoElCoche = () => {
  const fijo = raiz.querySelector('.pt-lado-escena');
  if (!fijo) return 0;
  // Se cuenta dónde quedará el coche UNA VEZ FIJO, no dónde está ahora: al entrar todavía no se ha pegado arriba.
  // En el móvil lo que tapa es el bloque del coche; en escritorio el coche va al lado y lo que tapa es la cabecera.
  const arriba = parseFloat(getComputedStyle(fijo).top) || 0;
  return getComputedStyle(fijo.parentElement).display === 'flex' ? arriba + fijo.offsetHeight : arriba;
};
filas.forEach(fila => {
  const titulo = fila.querySelector('summary');
  titulo.addEventListener('click', e => {
    e.preventDefault();
    const antes = titulo.getBoundingClientRect().top, abrir = !fila.open;
    filas.forEach(f => { f.open = f === fila && abrir; });
    window.scrollBy({ top: titulo.getBoundingClientRect().top - antes, behavior: 'instant' });
    if (!abrir) return;
    const sobra = Math.min(fila.getBoundingClientRect().bottom + 16 - window.innerHeight, titulo.getBoundingClientRect().top - bajoElCoche());
    if (sobra > 0) window.scrollBy({ top: sobra, behavior: 'smooth' });
  });
});

/* ---- el carrito: lo elegido entra de una vez en la cesta de la tienda ---- */
// Mou, 2026-10-01: «que se haga un carrito directamente con la selección en vez de que tenga que entrar a ver el
// kit o el parasol». Precio y variante no se guardan aquí: se preguntan a la tienda (/products/<handle>.js) cada
// vez que se elige algo, así que el precio que se enseña es siempre el de la ficha.
// Si la tienda no contesta —en la maqueta de este ordenador no puede—, el botón sale sin precio y avisa al pulsarlo.
const botonCarrito = $('.pv-carrito'), textoCarrito = $('.pv-carrito-texto'), avisoCarrito = $('.pv-carrito-aviso');
const ROTULO_CARRITO = textoCarrito ? textoCarrito.textContent : '';
const fichasTienda = {};
const fichaTienda = handle => fichasTienda[handle] || (fichasTienda[handle] =
  fetch((raiz.dataset.baseProducto || '/products/') + handle + '.js', { headers: { Accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : null)).catch(() => null));
// La variante que corresponde: la del color que dice el catálogo (`opcion`) o, si el producto no tiene colores, la única que hay.
const varianteDe = (p, d) => {
  if (d === estado.kit && coloresDe(d) && estado.colorKit) return varianteDeColor(p, d);
  return (p && (d.opcion ? p.variants.find(v => v.options.includes(d.opcion)) : p.variants[0])) || null;
};
// El kit en colores: la variante cuya opción se llama como el color elegido («Azul turquesa», «Plata espejo»…).
// No hay ningún identificador escrito aquí: se lee de la ficha. Mientras la ficha tenga una sola variante sin
// colores (hoy, 2026-10-07), solo se puede añadir el color con que se vende; con otro, el carrito manda a la ficha
// en vez de meter el kit en un color que el cliente no ha elegido.
const sinAcentos = s => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
function varianteDeColor(p, d) {
  if (!p) return null;
  const c = COLORES_KIT.find(k => k.id === estado.colorKit);
  const casa = o => c.alias.some(a => sinAcentos(o) === a) || sinAcentos(o).startsWith(c.alias[0]);
  const v = p.variants.find(x => (x.options || []).some(casa));
  if (v) return v;
  return p.variants.length === 1 && estado.colorKit === coloresDe(d).original ? p.variants[0] : null;
}
const enEuros = c => new Intl.NumberFormat('es-ES', { style: 'currency',
  currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR' }).format(c / 100);
let vueltaCarrito = 0;
async function pintarCarrito() {
  if (!botonCarrito) return;                        // el visor de pruebas y la página de colocar no llevan carrito
  const elegidos = [estado.parasol, estado.kit].filter(Boolean), vuelta = ++vueltaCarrito;
  botonCarrito.hidden = !elegidos.length;
  avisoCarrito.textContent = '';
  textoCarrito.textContent = ROTULO_CARRITO;
  estado.variantes = [];
  if (!elegidos.length) return;
  const variantes = await Promise.all(elegidos.map(d => fichaTienda(d.handle).then(p => varianteDe(p, d))));
  if (vuelta !== vueltaCarrito) return;             // mientras tanto se ha elegido otra cosa
  estado.variantes = variantes;
  // Si la ficha del kit ya tiene la variante del color elegido, el enlace la abre elegida (`variant=`, que es lo que
  // lee el tema). El id sale de la propia ficha; `color_kit` va siempre.
  const iKit = elegidos.indexOf(estado.kit), vKit = iKit >= 0 && coloresDe(estado.kit) && variantes[iKit];
  const ficha = $('.pv-ficha-kit');
  if (ficha && vKit && estado.colorKit && /color_kit=/.test(ficha.href)) ficha.href = ficha.href.replace(/(&variant=\d+)?$/, '&variant=' + vKit.id);
  if (variantes.every(Boolean)) textoCarrito.textContent = ROTULO_CARRITO + ' · ' + enEuros(variantes.reduce((s, v) => s + v.price, 0));
}
async function alCarrito() {
  const variantes = estado.variantes || [];
  if (!variantes.length || !variantes.every(Boolean)) { avisoCarrito.textContent = 'Desde aquí no se puede añadir ahora mismo. Entra en la ficha del producto, justo debajo.'; return; }
  if (variantes.some(v => !v.available)) { avisoCarrito.textContent = 'Uno de los productos no está disponible ahora mismo.'; return; }
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  botonCarrito.disabled = true;
  avisoCarrito.textContent = 'Añadiendo…';
  try {
    const r = await fetch(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantes.map(v => ({ id: v.id, quantity: 1 })) }) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.description || j.message || 'error ' + r.status);
    avisoCarrito.textContent = variantes.length > 1 ? 'Añadidos al carrito.' : 'Añadido al carrito.';
    // El tema tiene su cajón de carrito: se le pide que se refresque y se abra, y se avisa al contador de la cabecera.
    const cajon = document.querySelector('cart-drawer'), tema = window.theme;
    if (cajon && tema && tema.settings && tema.settings.cartType !== 'page') {
      if (tema.pubsub) fetch(base + 'cart.js').then(x => x.json())
        .then(cart => tema.pubsub.publish(tema.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'moa-personaliza', cart })).catch(() => {});
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    } else location.href = base + 'cart';
  } catch (err) {
    avisoCarrito.textContent = 'No se ha podido añadir al carrito (' + String(err.message).slice(0, 120) + ').';
  }
  botonCarrito.disabled = false;
}
if (botonCarrito) botonCarrito.addEventListener('click', alCarrito);

/* ---- los coches ---- */
// Un coche es su malla (mallas/<id>/<id>.glb) y las medidas de sus pieles (recursos/coches/<id>.json). La lista sale
// de recursos/coches/indice.json (data-coches). Sin lista, la página lleva un solo coche en data-glb y data-coche,
// como al principio. Cada coche se monta una vez y se guarda: volver a él no descarga nada.
let COCHES = [];
const montados = {};
let turnoCoche = null;
// Las mallas van comprimidas (Draco): sin comprimir, el E46 eran 8,3 MB. El descompresor son dos ficheros de
// three.js: en la maqueta los encuentra solo, al lado del cargador; en el tema se le dan por la tabla de rutas.
const draco = new DRACOLoader();
if (RUTAS['draco/draco_wasm_wrapper.js']) draco.setDecoderPath({ js: RUTAS['draco/draco_wasm_wrapper.js'], wasm: RUTAS['draco/draco_decoder.wasm'] });
const cargador = new GLTFLoader().setDRACOLoader(draco);

function montar(c, gltf, medidas) {
  const m = { grupo: new THREE.Group(), pieles: {}, defs: medidas.pieles, ppm: medidas.px_por_metro, franja: null, coche: gltf.scene };
  gltf.scene.traverse(o => {
    if (!o.isMesh) return;
    const nombre = (o.material && o.material.name) || '';
    if (MATERIALES[nombre]) o.material = MATERIALES[nombre];
    if (nombre === 'MOA_Cristal' && c.cristal_tintado) o.material = MATERIALES.MOA_Cristal_tintado;
    if (o.material === vinilo) { m.franja = o; o.visible = false; }
    // MOA_Piel_lateral_izq → clave «lateral_izq», zona «lateral», lado «izq». Las demás pieles cubren los dos lados.
    const clave = nombre.startsWith('MOA_Piel_') ? nombre.slice(9) : '', lado = (clave.match(/_(izq|der)$/) || [])[1] || '';
    const zona = clave.replace(/_(izq|der)$/, '');
    if (zona && m.defs[zona]) {
      const def = m.defs[zona], tela = document.createElement('canvas');
      tela.width = Math.ceil((def.u[1] - def.u[0]) * m.ppm);
      tela.height = Math.ceil((def.v[1] - def.v[0]) * m.ppm);
      const textura = new THREE.CanvasTexture(tela);
      textura.colorSpace = THREE.SRGBColorSpace;
      textura.flipY = false;
      textura.anisotropy = renderer.capabilities.getMaxAnisotropy();
      o.material = adhesivo.clone();
      o.material.map = textura;
      o.visible = false;
      m.pieles[clave] = { malla: o, tela, ctx: tela.getContext('2d'), textura, zona, lado, pintada: false };
    }
  });
  m.medidas = new THREE.Box3().setFromObject(gltf.scene).getSize(new THREE.Vector3());
  m.grupo.add(gltf.scene, FONDO_CLARO ? sombra(m.medidas.x * 1.5, m.medidas.z * 1.22) : sombra(m.medidas.x * 1.9, m.medidas.z * 1.5));
  return m;
}

// El crédito de la malla cambia con el coche: la licencia de cada una obliga a citar a su autor.
function ponerCredito(c) {
  const caja = $('.pv-credito'), k = c.credito;
  if (!caja || !k) return;
  const enlace = (texto, href) => { const a = document.createElement('a'); a.textContent = texto; a.href = href; a.rel = 'noopener'; return a; };
  caja.textContent = '';
  caja.append('Modelo 3D: ', enlace('«' + k.titulo + '»', k.url), ', de ', enlace(k.autor, k.autor_url), ', con licencia ', enlace(k.licencia, k.licencia_url), '. Modificado.');
}

function usar(c, m) {
  if (estado.montado) escena.remove(estado.montado.grupo);
  escena.add(m.grupo);
  Object.keys(pieles).forEach(k => delete pieles[k]);
  Object.assign(pieles, m.pieles);
  defs = m.defs;
  pxPorMetro = m.ppm;
  Object.assign(estado, { montado: m, cocheId: c.id, coche: m.coche, franja: m.franja, medidas: m.medidas });
  raiz.querySelectorAll('.pv-chip[data-id]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === c.id)));
  if (c.marca) verGrupo($('.pv-coches'), c.marca);        // la pestaña abierta es la de la marca del coche que se ve
  const nombre = $('.pv-coche-nombre');
  if (nombre && c.nombre) nombre.textContent = c.nombre;
  ponerCredito(c);
  raiz.querySelectorAll('.pv-kit').forEach(b => { const d = KITS.find(k => k.id === b.dataset.id); b.hidden = !!d && !piezasDe(d); });
  estadoTexto.hidden = true;
  soloDeLaMarca(c);
  if (deOtraMarca(estado.parasol, c)) estado.parasol = null;       // el parasol de BMW no se queda puesto en un Golf
  if (deOtraMarca(estado.kit, c)) estado.kit = null;
  ponerParasol(estado.parasol);
  ponerKit(estado.kit);              // el mismo kit, colocado como va en ESTE coche; si aquí no lo hay, se quita
  pedir();
}

// El coche elegido se recuerda entre visitas: el botón «Pruébalo en tu coche» de cada ficha trae el kit o el parasol y
// lo pone en el coche que el cliente eligió la última vez; si nunca eligió, en el primero del índice (Mou, 2026-10-05).
const COCHE_GUARDADO = 'moa-pv-coche';
const cocheGuardado = () => { try { return localStorage.getItem(COCHE_GUARDADO); } catch (e) { return null; } };
// Solo se guarda el que el cliente elige con su botón: si el configurador cambia de coche por su cuenta (un kit de BMW
// abierto teniendo guardado un Golf), el Golf sigue siendo «su coche». Devuelve undefined para encadenarse con ||.
const guardarCoche = c => { try { localStorage.setItem(COCHE_GUARDADO, c.id); } catch (e) { /* sin almacenamiento */ } };

function ponerCoche(c) {
  if (estado.cocheId === c.id) return Promise.resolve();
  const turno = turnoCoche = {};
  if (montados[c.id]) { usar(c, montados[c.id]); return Promise.resolve(); }
  estadoTexto.textContent = 'Cargando el coche';
  estadoTexto.hidden = false;
  return Promise.all([cargador.loadAsync(c.glb), fetch(c.medidas).then(r => r.json())]).then(([gltf, medidas]) => {
    montados[c.id] = montar(c, gltf, medidas);
    if (turnoCoche === turno) usar(c, montados[c.id]);       // si mientras cargaba se eligió otro, este se queda guardado
  });
}

/* ---- filtros: una fila de pestañas encima de una bandeja, para que no se llene ---- */
// Mou, 2026-10-02: «generarás un filtro de marca y de productos, para que no se saturen los menús». Con 30 coches
// y cientos de parasoles la bandeja entera no cabe en un móvil. Cada opción lleva `data-grupo`; la pestaña elegida
// esconde las de los demás grupos. «Sin parasol» y «Sin kit» no llevan grupo y se ven siempre.
function filtro(caja, grupos, nombre, conTodos) {
  if (!caja || grupos.length < 2) return null;
  const fila = document.createElement('div');
  fila.className = 'pv-filtro'; fila.setAttribute('role', 'group'); fila.setAttribute('aria-label', nombre);
  // «Todos» va la última: con casi cien parasoles, abrir enseñándolos todos es justo el menú saturado que se evita.
  grupos.concat(conTodos ? [''] : []).forEach(g => {
    const b = document.createElement('button');
    b.type = 'button'; b.dataset.grupo = g; b.textContent = g || 'Todos'; b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', () => verGrupo(caja, g));
    fila.appendChild(b);
  });
  caja.appendChild(fila);
  return fila;
}
function verGrupo(caja, g) {
  if (!caja || !caja.querySelector('.pv-filtro')) return;
  caja.querySelectorAll('.pv-filtro button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.grupo === g)));
  [...caja.children].forEach(b => { if (b.dataset && 'grupo' in b.dataset && !b.closest('.pv-filtro')) b.classList.toggle('pv-fuera', !!g && b.dataset.grupo !== g); });
}

// Mou, 2026-10-02: «que los de X marca solo aparezcan cuando seleccionamos un vehículo de esa marca, para evitar
// llenar mucho el listado». El parasol o el kit que lleva `marca` (BMW, Audi, Seat…) solo se ofrece con un coche de
// esa marca; los demás (escena, videojuegos, llamas) se ofrecen siempre. La pestaña que se queda sin nada, se esconde.
// El coche con el que se abre un producto de marca: el más vistoso de los que hay de esa marca (elegido por Claude el
// 2026-10-05, a petición de Mou: «que empiecen con el coche más bonito»). Una marca que no esté aquí abre su primero.
const MAS_BONITO = {
  bmw: 'e30', audi: 'tt8j', volkswagen: 'golf7', seat: 'leon3', ford: 'focusrs2', fiat: 'abarth500', kia: 'stinger',
  mazda: 'rx7fd', mitsubishi: 'evo10b', renault: 'megane3rs', peugeot: 'p205', toyota: 'supra4', subaru: 'wrx96',
  honda: 'nsx1', opel: 'astraj', mercedes: 'cls218', 'citroën': 'ds3', nissan: 'r34', mini: 'minir56',
  hyundai: 'i30n', cupra: 'formentor'
};
const deOtraMarca = (d, c) => !!(d && d.marca && String(d.marca).toLowerCase() !== String((c && c.marca) || '').toLowerCase());
function soloDeLaMarca(c) {
  const marca = String(c.marca || '').toLowerCase();
  [$('.pv-tiras'), $('.pv-kits')].forEach(caja => {
    if (!caja) return;
    const opciones = [...caja.children].filter(b => b.dataset && !b.classList.contains('pv-filtro'));
    opciones.forEach(b => b.classList.toggle('pv-otra-marca', !!b.dataset.marca && b.dataset.marca.toLowerCase() !== marca));
    const fila = caja.querySelector('.pv-filtro');
    if (!fila) return;
    let abierta = null;
    fila.querySelectorAll('button').forEach(t => {
      const g = t.dataset.grupo;
      t.hidden = !!g && !opciones.some(b => b.dataset.grupo === g && !b.classList.contains('pv-otra-marca'));
      if (t.getAttribute('aria-pressed') === 'true') abierta = t;
    });
    if (abierta && abierta.hidden) verGrupo(caja, (fila.querySelector('button:not([hidden])') || { dataset: { grupo: '' } }).dataset.grupo);
  });
}

function pintarCoches() {
  const caja = $('.pv-coches');
  if (!caja || !raiz.dataset.coches) return;
  caja.textContent = '';
  const marcas = [...new Set(COCHES.map(c => c.marca).filter(Boolean))];
  filtro(caja, marcas, 'Marca', false);
  COCHES.forEach(c => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'pv-chip'; b.dataset.id = c.id;
    // Dentro de la pestaña de su marca, el nombre va sin ella: «Serie 3 E36», no «BMW Serie 3 E36».
    if (c.marca && marcas.length > 1) { b.dataset.grupo = c.marca; b.textContent = c.nombre.replace(c.marca + ' ', ''); }
    else b.textContent = c.nombre;
    b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', () => guardarCoche(c) || ponerCoche(c).catch(e => {
      console.error(e);
      estadoTexto.hidden = true;
      if (avisoCarrito) avisoCarrito.textContent = 'No se ha podido cargar ese coche. Prueba otra vez.';
    }));
    caja.appendChild(b);
  });
}

/* ---- arranque ---- */
const listaDeCoches = raiz.dataset.coches
  // La marca, si el índice no la dice, es la primera palabra del nombre («BMW Serie 3 E36» → «BMW»).
  ? fetch(raiz.dataset.coches).then(r => r.json()).then(j => ({ primero: j.primero, coches: j.coches.map(c => ({ ...c,
    marca: c.marca || String(c.nombre || '').split(' ')[0],
    glb: RUTAS['mallas/' + c.id + '.glb'] || (raiz.dataset.baseMallas || '../mallas/') + c.id + '/' + c.id + '.glb',
    medidas: ruta('coches/' + c.id + '.json') })) }))
  : Promise.resolve({ coches: [{ id: (/([\w-]+)\.json/.exec(raiz.dataset.coche) || [0, 'e36'])[1], glb: raiz.dataset.glb, medidas: raiz.dataset.coche }] });
Promise.all([
  listaDeCoches,
  fetch(raiz.dataset.catalogo).then(r => r.json()),
  // Los colores de cada kit: en el tema van dentro de cada kit (`colores`, lo mete empaquetar_tema.py) y no hay
  // fichero aparte; en la maqueta se leen de recursos/kits/colores.json, al lado de kits.json.
  fetch(raiz.dataset.kits).then(r => r.json()).then(j => j.kits.some(k => k.colores) ? j.kits
    : fetch(raiz.dataset.kitsColores || raiz.dataset.kits.replace(/kits\.json$/, 'colores.json'))
      .then(r => (r.ok ? r.json() : {})).catch(() => ({}))
      .then(c => { COLORES_DE_KIT = (c && c.kits) || {}; return j.kits; }))
]).then(([lista, catalogo, kits]) => {
  COCHES = lista.coches;
  pintarCoches();
  pintarControles(catalogo);
  pintarKits(kits);
  // Se entra con el coche en blanco (Mou, 2026-10-05): es como salen las fotos de las fichas.
  ponerColor(COLORES.find(c => c.id === q.get('color')) || COLORES.find(c => c.id === 'blanco'));
  // El parasol y el kit de la dirección se quedan apuntados: se ponen al montar el coche, que es quien tiene dónde.
  estado.parasol = catalogo.find(d => d.id === q.get('parasol')) || null;
  estado.kit = kits.find(d => d.id === q.get('kit')) || null;
  // `color_kit=` (azul, plata-espejo…) entra con ese color; si no viene o no vale, con el que se vende.
  const cfgKit = coloresDe(estado.kit);
  estado.colorKit = cfgKit ? (COLORES_KIT.some(k => k.id === q.get('color_kit')) ? q.get('color_kit') : cfgKit.original) : null;
  // Entrando con un kit claro (desde la ficha), el coche ya sale negro; salvo que la dirección traiga `color=`.
  if (!q.has('color')) negroSiClaro();
  // Con qué coche se entra (Mou, 2026-10-05): «los productos universales que empiecen con el BMW E30 blanco. Los de
  // marca no, que empiecen con el coche más bonito» de esa marca. Ya no se abre con el último coche elegido: manda
  // esto. `?coche=` sigue mandando sobre todo (lo usan las pruebas y los enlaces hechos a mano).
  const vale = c => c && !deOtraMarca(estado.kit, c) && !deOtraMarca(estado.parasol, c);
  const marcaPedida = (estado.kit && estado.kit.marca) || (estado.parasol && estado.parasol.marca);
  const deLaMarca = m => COCHES.find(c => c.id === MAS_BONITO[String(m).toLowerCase()])
    || COCHES.find(c => String(c.marca).toLowerCase() === String(m).toLowerCase());
  const candidatos = [COCHES.find(c => c.id === q.get('coche')), marcaPedida && deLaMarca(marcaPedida),
    COCHES.find(c => c.id === lista.primero)];
  return ponerCoche(candidatos.find(vale) || COCHES[0]);
}).then(() => {
  encuadrar();
  estadoTexto.hidden = true;
  estado.listo = true;
  bucle();
}).catch(e => {
  estadoTexto.textContent = 'No se ha podido cargar el coche. Recarga la página.';
  console.error(e);
});

window.MoaVisor = { estado, pieles, camara, controles, pintura, vinilo, adhesivo, COLORES, COLORES_KIT, ponerColor, ponerParasol, ponerKit, ponerColorKit, coloresDe, ponerCoche, coches: () => COCHES,
  pintarPieles, aPx, aMetros, reflejo, caraDe, pedir, lienzo, pxPorMetro: () => pxPorMetro, defs: () => defs,
  cerca, pintarCarrito, alCarrito, cuadros: () => renderer.info.render.frame };
